import { ReactNode } from 'react';
import { Logo } from '@rossum/ui';
import { Box, keyframes, Stack, Typography } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { PADDING_TOP } from '../../../components/UI/Container';

export const HEADER_HEIGHT = 50;

const LOGO_SIZE = 30;

const ANIMATION_DURATION = 2000;
const ANIMATION_DELAY = 1000;

const logoAnimation = keyframes({
  '0%': { left: '-100%' },
  '100%': { left: '-50%' },
});

const textAnimation = keyframes({
  '0%': { opacity: 1 },
  '8%': { opacity: 0 },
  '100%': { opacity: 0 },
});

type TextWrapperProps = {
  children: ReactNode;
  isLoading: boolean;
};
const TextWrapper = ({ children, isLoading }: TextWrapperProps) => {
  return (
    <Box
      sx={{
        marginLeft: theme => `calc(${theme.spacing(1.5)} + ${LOGO_SIZE}px)`,
        animationName: isLoading ? `${textAnimation}` : '',
        animationDuration: `${ANIMATION_DURATION}ms`,
        animationDelay: `${ANIMATION_DELAY}ms`,
        animationIterationCount: 1,
        animationFillMode: 'forwards',
      }}
    >
      {children}
    </Box>
  );
};

type OverlayProps = {
  isLoading: boolean;
};
const Overlay = ({ isLoading }: OverlayProps) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        backgroundColor: theme => theme.palette.background.default,
        opacity: isLoading ? 1 : 0,
      }}
    />
  );
};

export type AnimationState = 'ready' | 'loading' | 'hidden';

type AnimatedHeaderProps = {
  title: string;
  animationState: AnimationState;
  setAnimationDone: (value: boolean) => void;
};

const AnimatedHeader = ({
  title,
  animationState,
  setAnimationDone,
}: AnimatedHeaderProps) => {
  const isLoading = animationState === 'loading';

  return (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      sx={{
        position: 'fixed',
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        pointerEvents: 'none',
      }}
    >
      <Overlay isLoading={isLoading} />
      <Stack
        alignItems="center"
        direction="row"
        sx={{
          position: 'absolute',
          top: PADDING_TOP,
          color: theme => theme.palette.text.primary,
        }}
      >
        <Stack
          onAnimationEnd={() => setAnimationDone(true)}
          sx={{
            position: 'absolute',
            left: isLoading ? '-50%' : '-100%',
            padding: `5px 0 5px 100%`,
            backgroundColor: theme => theme.palette.background.default,
            borderTopRightRadius: theme =>
              borderRadius.abs(theme.shape.borderRadius, 'xl'),
            borderBottomRightRadius: theme =>
              borderRadius.abs(theme.shape.borderRadius, 'xl'),
            zIndex: 1,
            animationName: isLoading ? `${logoAnimation}` : '',
            animationDuration: `${ANIMATION_DURATION}ms`,
            animationIterationCount: 1,
            animationTimingFunction: 'cubic-bezier(.3,-1,.4,4)',
          }}
        >
          <Logo size={LOGO_SIZE} />
        </Stack>
        <TextWrapper isLoading={isLoading}>
          <Typography variant="h3">{title}</Typography>
        </TextWrapper>
      </Stack>
    </Stack>
  );
};

export default AnimatedHeader;
